import { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { movies, movieCategories } from '@/mocks/movies';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'latest' | 'rating' | 'views'>('latest');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const currentCategory = useMemo(
    () => movieCategories.find((c) => c.slug === slug),
    [slug]
  );

  const filteredMovies = useMemo(() => {
    let result = movies.filter((m) => m.categorySlug === slug);
    switch (sortBy) {
      case 'rating':
        result = [...result].sort((a, b) => b.rating - a.rating);
        break;
      case 'views':
        result = [...result].sort((a, b) => b.viewCount - a.viewCount);
        break;
      default:
        result = [...result].sort((a, b) => b.year - a.year);
    }
    return result;
  }, [slug, sortBy]);

  const categoryName = currentCategory?.name || slug || '';
  const pageTitle = `${categoryName} - 6080新视觉影院免费在线观看`;
  const pageDesc = `6080新视觉影院${categoryName}频道，提供最新${categoryName}高清免费在线观看，不卡顿流畅播放，极速秒开不缓冲，免费看${categoryName}大全。`;

  const breadcrumbListSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: typeof window !== 'undefined' ? window.location.origin + '/' : '/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: categoryName,
        item: typeof window !== 'undefined' ? window.location.origin + `/category/${slug}` : `/category/${slug}`,
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbListSchema) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200">
            <div className="w-full px-4 md:px-6 py-8 md:py-12">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4" aria-label="面包屑导航">
                <Link to="/" className="hover:text-accent-600 transition-colors">首页</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-900 font-medium">{categoryName}</span>
              </nav>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-[var(--font-heading)]">
                {categoryName}
                <span className="text-foreground-400 text-lg font-normal ml-2">
                  共 {filteredMovies.length} 部
                </span>
              </h1>
            </div>
          </div>

          <div className="w-full px-4 md:px-6 py-6 md:py-8">
            <div className="flex flex-wrap items-center gap-2 mb-6">
              {movieCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="flex items-center gap-3 mb-6">
              <span className="text-sm text-foreground-500">排序：</span>
              {[
                { key: 'latest', label: '最新上映' },
                { key: 'rating', label: '评分最高' },
                { key: 'views', label: '最受欢迎' },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key as typeof sortBy)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    sortBy === opt.key
                      ? 'bg-foreground-950 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5" data-product-shop="">
              {filteredMovies.map((movie) => (
                <Link
                  key={movie.id}
                  to={`/article/${movie.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-[2/3] rounded-lg overflow-hidden mb-3">
                    <img
                      src={movie.posterUrl}
                      alt={`${movie.title} ${movie.year} ${movie.category}`}
                      title={`${movie.title} ${categoryName}在线观看`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-2 left-2">
                      <span className="px-2 py-0.5 text-[10px] font-medium bg-accent-500 text-background-50 rounded-full whitespace-nowrap">
                        {movie.rating}
                      </span>
                    </div>
                    {movie.isNew && (
                      <div className="absolute top-2 right-2">
                        <span className="px-2 py-0.5 text-[10px] font-medium bg-green-500 text-white rounded-full whitespace-nowrap">
                          NEW
                        </span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4">
                      <span className="px-4 py-1.5 bg-accent-500 text-background-50 rounded-full text-xs font-medium whitespace-nowrap">
                        <i className="ri-play-circle-line mr-1"></i>观看影片
                      </span>
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-900 group-hover:text-accent-600 transition-colors line-clamp-1">
                    {movie.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-1">
                    {movie.year} · {movie.region}
                  </p>
                </Link>
              ))}
              {filteredMovies.length === 0 && (
                <div className="col-span-full text-center py-16 text-foreground-400">
                  <i className="ri-film-line text-4xl block mb-3"></i>
                  <p className="text-sm">该分类暂无影片</p>
                </div>
              )}
            </div>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}